/**
 * useFamilyMember Hook
 *
 * Manages state for a single family member by ID.
 * Auto-loads member on mount and provides refresh capability.
 * Uses scoped storage for device/user-specific data.
 */

import { useState, useEffect, useCallback } from 'react';
import { FamilyMember, UpdateFamilyMemberInput } from '@/types/family-member';
import { useFamilyMemberManager } from '@/hooks/use-scoped-managers';

type UseFamilyMemberResult = {
  member: FamilyMember | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateMember: (updates: UpdateFamilyMemberInput) => Promise<FamilyMember | null>;
  deleteMember: () => Promise<boolean>;
};

export function useFamilyMember(id: string | null): UseFamilyMemberResult {
  const manager = useFamilyMemberManager();
  const [member, setMember] = useState<FamilyMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMember = useCallback(async () => {
    if (!id) {
      setMember(null);
      setIsLoading(false);
      return;
    }

    if (!manager) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const loadedMember = await manager.getById(id);
      setMember(loadedMember);
    } catch (err) {
      console.error('Error loading family member:', err);
      setError(err instanceof Error ? err.message : 'Failed to load family member');
    } finally {
      setIsLoading(false);
    }
  }, [id, manager]);

  useEffect(() => {
    if (manager) {
      loadMember();
    }
  }, [manager, loadMember]);

  const updateMember = useCallback(async (
    updates: UpdateFamilyMemberInput
  ): Promise<FamilyMember | null> => {
    if (!id) return null;
    if (!manager) {
      throw new Error('Manager not initialized');
    }

    const updated = await manager.update(id, updates);
    if (updated) {
      setMember(updated);
    }
    return updated;
  }, [id, manager]);

  const deleteMember = useCallback(async (): Promise<boolean> => {
    if (!id) return false;
    if (!manager) {
      throw new Error('Manager not initialized');
    }

    const success = await manager.delete(id);
    if (success) {
      setMember(null);
    }
    return success;
  }, [id, manager]);

  return {
    member,
    isLoading,
    error,
    refresh: loadMember,
    updateMember,
    deleteMember,
  };
}
